import {
  ToolCallData,
  ToolCallLifecycleStatus,
} from '@shared/services/conversationApiService/types';
import { isRecord, isToolCallData } from '@shared/utils/conversationLog.utils';

export { isRecord, isToolCallData } from '@shared/utils/conversationLog.utils';

export const parseToolCallPacket = (payload: Uint8Array): ToolCallData | null => {
  try {
    const event: unknown = JSON.parse(new TextDecoder().decode(payload));

    if (!isRecord(event) || event.type !== 'tool_call' || !isToolCallData(event.toolCall)) {
      return null;
    }

    return event.toolCall;
  } catch {
    return null;
  }
};

export type ToolCallUiStatus = 'success' | 'failed' | 'cancelled';

const UI_STATUS_BY_LIFECYCLE: Record<ToolCallLifecycleStatus, ToolCallUiStatus> = {
  done: 'success',
  error: 'failed',
  cancelled: 'cancelled',
};

export const getToolCallUiStatus = (status: ToolCallLifecycleStatus): ToolCallUiStatus =>
  UI_STATUS_BY_LIFECYCLE[status];

export const getToolCallStatusLabel = (
  status: ToolCallLifecycleStatus,
  t: (key: string) => string,
): string => t(`campaignPage.toolCall.status.${getToolCallUiStatus(status)}`);

export const hasToolCallResult = (toolCall: ToolCallData): boolean =>
  Boolean(toolCall.function_call_output?.output.trim());

export const getToolCallDuration = (toolCall: ToolCallData): number => {
  const duration = new Date(toolCall.endedAt).getTime() - new Date(toolCall.startedAt).getTime();
  return Number.isFinite(duration) ? Math.max(0, duration) : 0;
};

export const formatToolCallValue = (value: string): string => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

export const parseToolCallArguments = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const formatToolCallArgumentLabel = (value: string): string => {
  const label = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return label ? label.charAt(0).toUpperCase() + label.slice(1) : value;
};

export const formatToolCallScalar = (value: unknown, t: (key: string) => string): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') {
    return t(value ? 'campaignPage.toolCall.yes' : 'campaignPage.toolCall.no');
  }
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  return formatToolCallValue(JSON.stringify(value));
};

// Fixed reason codes sent by built-in tools (e.g. call_handoff_signal).
const KNOWN_REASON_CODES = new Set(['transfer', 'escalate']);
// Placeholder the worker uses when the LLM gave no reason.
const EMPTY_REASON_CODES = new Set(['default']);

export const getToolCallReason = (toolCall: ToolCallData, t: (key: string) => string): string => {
  const args = parseToolCallArguments(toolCall.function_call.arguments);
  const reason = isRecord(args) && typeof args.reason === 'string' ? args.reason.trim() : '';
  const code = reason.toLowerCase();

  if (reason && !EMPTY_REASON_CODES.has(code)) {
    return KNOWN_REASON_CODES.has(code) ? t(`campaignPage.toolCall.reason.${code}`) : reason;
  }

  return t(`campaignPage.toolCall.reason.${toolCall.lifecycleStatus}`);
};
